/**
 * The shadow stage's exit bar, per scenario (TA-89 addendum, decision list
 * item 28).
 *
 * After M5 real customers start in shadow: the agent composes a draft on
 * every live inbound and nothing is sent. The plan's stage 3
 * (docs/PLAN-TO-2026-09-30.md) ends it per scenario when:
 *
 *  - the window is met, 30 conversations or 7 days;
 *  - there are zero invariant breaches;
 *  - there are zero figure mismatches (B13);
 *  - the grounding floor holds.
 *
 * The numbers are the vault's. The Training & Auto-Learn Master Spec's B6 v1
 * defaults table reads "Challenger shadow | 30 conversations or 7 days,
 * whichever first" and "Grounding-score floor | >= 95% on eval set". Whichever
 * first means either suffices, so the window is met on either.
 *
 * WHAT THIS IS NOT. It is a pure decision over evidence someone else read
 * (packages/db/src/shadowEvidence.ts). It writes nothing, returns no autonomy
 * standing and applies no trigger: thresholds qualify, the human flips the
 * switch (B6). `applyAutonomyTrigger` (autonomy.ts) is never called from here.
 *
 * THE RULES THAT OUTRANK THE BARS (section 0).
 *  - Unknown or unreadable evidence is a blocker, never a pass. A draft whose
 *    stored first pass cannot be read, a composer that is a mock or was never
 *    recorded (TA-430), an invariant that is not recorded: each blocks, with
 *    the draft ids, in words.
 *  - Class D (complaints, fraud-suspect, unknown) never qualifies, whatever
 *    the evidence says (Agent Brains section 9, CLAUDE.md section 3). The one
 *    "n/a" row, vendor_spam, generates nothing and has nothing to measure.
 *  - A draft no scenario can be named for, carrying anything short of clean
 *    evidence, blocks every scenario: none of them can be cleared of it.
 *
 * THE FOUR BARS, AS MEASURED.
 *  - Window: distinct threads among the scenario's measured drafts, and the
 *    span from its first draft to its last. The span, not "since shadow
 *    began": the start is stored now (`shadow.started`, decision list item
 *    70), but it only bounds which drafts `pnpm measure:shadow` reads, and a
 *    span between evidence is the stricter reading TA-214 took for the
 *    observation span.
 *
 *    A compose model or approved-artifact change restarts it (decision list
 *    item 75, section 11, Kabir's yes, 2026-09-29). The window counts only the
 *    latest run: the drafts after the last one composed on any other model or
 *    artifact versions. The restart is the client's, since a change reaches
 *    every scenario at once, so a scenario with no draft since the change has
 *    nothing in its window. A draft whose model or versions are not recorded
 *    may hide a change, so it ends a run like a change does, and when the
 *    latest draft is one, no window can start. The escalation sweep's holding
 *    line (decision list items 78a and 84a) stores no model because none
 *    wrote it, so it neither names nor ends a run and is not counted in any
 *    window (TA-467). Only the window restarts: the other three bars still
 *    read every draft in the measured period.
 *  - Invariant breaches, on the output as stored (the eval's precedent, where
 *    `verifier_clean` and `no_ai_tells` are checked on the final output):
 *    the verifier refused it; an owner released it, when the stage says
 *    nothing is sent; anything left the building; recorded AI tells. Compose
 *    stores the tells from migration 0033 (decision list item 69); a draft
 *    written before it has none, so `no_ai_tells` is unknown on it and
 *    blocks.
 *  - Figure mismatches (B13): a figure code on the stored verifier result,
 *    or on the first pass's, which is what counts (F-25). Compose stores the
 *    first pass's result from migration 0033. A draft written before it whose
 *    first pass the verifier rejected and whose retry passed has a first-pass
 *    figure status nobody can read. That is a blocker, not a zero.
 *  - Grounding: first-pass clean over everything the scenario composed on,
 *    on exact counts (TA-241). A routed inbound that produced no draft counts
 *    against it, as a failed sample does in the eval's first-pass consistency.
 *    A holding line is not composed and counts on neither side (TA-467): a
 *    scenario does not qualify on lines no model wrote.
 *
 * THE OWNER'S ECHO. Each draft is paired with the owner's answer to the same
 * inbound (TA-396's rule) and the pairs are counted. Nothing is scored for
 * similarity to one reply (F-22); the voice-fidelity judge is Deferred
 * (SPEC_COVERAGE B14). Where the two carry different figures it is listed,
 * by draft id, and it is not a bar: B13's mismatch is against the KB, and the
 * owner's reply is not a KB source.
 */

import { DAY_MS } from "./window";
import { SCENARIO_MAP, type Scenario, type ScenarioClass } from "./scenario";

export const SHADOW_EXIT_BARS = {
  /** B6 v1 defaults, "Challenger shadow | 30 conversations or 7 days, whichever first". */
  minConversations: 30,
  minSpanMs: 7 * DAY_MS,
  /**
   * B6 v1 default, "Grounding-score floor | >= 95%". A whole percent so the
   * comparison is on exact counts (TA-241); evals' `GROUNDING_FLOOR` is the
   * same number, held by packages/evals/src/shadowFloor.test.ts.
   */
  groundingFloorPercent: 95,
} as const;

/**
 * What `describeDraftArtifactVersions` (packages/db/src/artifactSource.ts)
 * returns for stored versions it cannot read. Here so the window can tell it
 * from a readable label: it names no configuration.
 */
export const SHADOW_ARTIFACT_VERSIONS_UNREADABLE = "unreadable";

/** The verifier's number violations (packages/agents/src/verifier.ts): what B13 calls a figure mismatch. */
export const SHADOW_FIGURE_CODES = ["ungrounded_number", "stale_price", "memory_sourced_price"] as const;
export type ShadowFigureCode = (typeof SHADOW_FIGURE_CODES)[number];

export const SHADOW_BREACH_KINDS = ["verifier_refused", "released", "left_the_building", "ai_tells"] as const;
export type ShadowBreachKind = (typeof SHADOW_BREACH_KINDS)[number];

/** An owner release: F-06's `sent` and everything after it. The stage says nothing is sent. */
const RELEASED_STATUSES: ReadonlySet<string> = new Set(["approved", "sent", "transmitted"]);

export type ShadowEchoEvidence =
  | { readonly paired: false }
  | { readonly paired: true; readonly figuresDiffer: boolean };

export interface ShadowVerifierEvidence {
  readonly ok: boolean;
  readonly violations: readonly string[];
}

/** One composed draft, as stored. No text: the reader keeps it. */
export interface ShadowDraftEvidence {
  readonly draftId: string;
  readonly threadId: string;
  /** From the `routing.decided` event for the exact inbound the draft answers; null when there is none. */
  readonly scenario: string | null;
  readonly createdAt: Date;
  readonly agentVersion: string;
  /** False for a mock or an unrecorded composer (`isMockDraft`, TA-430). */
  readonly provenReal: boolean;
  readonly status: string;
  readonly firstPassClean: boolean;
  /** Length of the stored attempts array; null when it is not an array. */
  readonly parsedAttempts: number | null;
  /** The LAST attempt's verifier result; null when it cannot be read. */
  readonly verifier: ShadowVerifierEvidence | null;
  /**
   * The FIRST attempt's verifier result (migration 0033, decision list item
   * 69). Null when it was not recorded, or when the first attempt never
   * parsed and so never reached the verifier.
   */
  readonly firstPassVerifier: ShadowVerifierEvidence | null;
  /** AI tells on the stored body; null when not recorded (drafts before migration 0033). */
  readonly tells: number | null;
  /** The model the stored body's compose call answered with; null when not recorded. */
  readonly composeModel: string | null;
  /** The artifact versions it was composed from, as one printable label; null when not recorded. */
  readonly artifactVersions: string | null;
  /**
   * The escalation sweep's fixed line (decision list items 78a and 84a,
   * migration 0034): no model wrote it. Null when not recorded (drafts before
   * 0034), which reads as composed, so a missing model still ends a run.
   */
  readonly holdingLine: boolean | null;
  /**
   * Migration 0035 (decision list item 97a): code appended the fixed contact
   * ask in grace. Null when not recorded (drafts before 0035).
   */
  readonly contactAskAppended: boolean | null;
  /** Where code appended the ask, whether the model had asked too: a suspected double ask. Null when not appended. */
  readonly modelAskedToo: boolean | null;
  /** A transmitted status, a `transmitted_at`, or a transmit claim that may have gone. */
  readonly leftTheBuilding: boolean;
  readonly echo: ShadowEchoEvidence;
}

/** A routed inbound that produced no draft at all. */
export interface ShadowUndraftedEvidence {
  readonly threadId: string | null;
  readonly messageId: string | null;
  readonly scenario: string | null;
  readonly routedAt: Date;
}

export interface ShadowEvidence {
  readonly drafts: readonly ShadowDraftEvidence[];
  readonly undrafted: readonly ShadowUndraftedEvidence[];
}

export interface ShadowScenarioExit {
  readonly scenario: Scenario;
  readonly graduationClass: ScenarioClass;
  readonly qualifies: boolean;
  readonly window: {
    readonly conversations: number;
    readonly spanMs: number | null;
    readonly qualifies: boolean;
    /** Measured drafts before the latest run (decision list item 75): read by the other bars, not counted here. */
    readonly notCounted: number;
  };
  readonly invariants: {
    readonly breaches: number;
    readonly byKind: Readonly<Record<ShadowBreachKind, number>>;
    readonly breachDraftIds: readonly string[];
    readonly tellsUnrecorded: number;
    readonly qualifies: boolean;
  };
  readonly figures: {
    readonly mismatches: number;
    readonly mismatchDraftIds: readonly string[];
    readonly firstPassUnrecorded: number;
    readonly firstPassUnrecordedDraftIds: readonly string[];
    readonly qualifies: boolean;
  };
  readonly grounding: { readonly firstPassClean: number; readonly total: number; readonly qualifies: boolean };
  /**
   * TA-1266 (F-25 data, no behaviour change): composed drafts whose first
   * pass the Voice gate refused (R7b, `voice_off_band` on the stored first
   * verifier result), alone or beside another code. They already count as not
   * first-pass clean in `grounding`; this only makes the share visible for
   * Kabir's decision on whether voice belongs in the grounding floor (review
   * r7b I4). No bar reads it.
   */
  readonly voice: { readonly firstPassRefused: number; readonly draftIds: readonly string[] };
  readonly drafts: {
    readonly measured: number;
    readonly byStatus: Readonly<Record<string, number>>;
    readonly notProvenReal: number;
    readonly notProvenRealDraftIds: readonly string[];
    readonly unreadable: number;
    readonly unreadableDraftIds: readonly string[];
    readonly undrafted: number;
    /** Measured holding lines (TA-467): read for breaches and figures, counted toward neither the window nor grounding. */
    readonly holdingLines: number;
  };
  readonly echo: {
    readonly paired: number;
    readonly unpaired: number;
    readonly figuresDiffer: number;
    readonly figuresDifferDraftIds: readonly string[];
  };
  /**
   * Decision list item 108 (TA-500): where code appended the fixed contact
   * ask, and where the model had asked too. A signal, never a bar: it adds no
   * blocker and moves no qualify.
   */
  readonly contactAsk: {
    readonly appended: number;
    readonly askedToo: number;
    readonly askedTooDraftIds: readonly string[];
    /** Drafts that do not record whether code appended the ask (before migration 0035). */
    readonly notRecorded: number;
  };
  readonly agentVersions: readonly string[];
  /**
   * Distinct compose models and artifact version labels, "not recorded" for a
   * draft that stored none. The lists are reported; a change restarts the
   * window (decision list item 75, `ShadowConfiguration`).
   */
  readonly composeModels: readonly string[];
  readonly artifactVersions: readonly string[];
  /** Every reason it does not qualify, in words. Empty exactly when it qualifies. */
  readonly blockers: readonly string[];
}

/** The configuration the latest draft ran on, and where its run began (decision list item 75). */
export interface ShadowConfiguration {
  readonly composeModel: string | null;
  readonly artifactVersions: string | null;
  /** Both recorded and readable. When false no run can be shown to share them, and no window starts. */
  readonly recorded: boolean;
  /** The first draft of the latest run: where every scenario's window starts. Null when not recorded. */
  readonly since: Date | null;
}

export interface ShadowExitReport {
  readonly scenarios: readonly ShadowScenarioExit[];
  /** Null when no draft has a readable time. */
  readonly configuration: ShadowConfiguration | null;
  readonly unattributed: {
    readonly drafts: number;
    readonly undrafted: number;
    /** Names a routing event carried that the scenario map does not know. */
    readonly unrecognizedScenarios: readonly string[];
    /** Unattributed drafts whose evidence is anything short of clean. */
    readonly blockingDraftIds: readonly string[];
  };
  /** Every unreadable draft, attributed or not, with the reason. The command exits 1 on any. */
  readonly unreadable: readonly { readonly draftId: string; readonly reason: string }[];
  readonly qualifying: readonly Scenario[];
  readonly voiceFidelityJudge: "deferred";
}

interface Reading {
  readonly draft: ShadowDraftEvidence;
  readonly unreadable: string | null;
  readonly breaches: readonly ShadowBreachKind[];
  readonly figureMismatch: boolean;
  readonly firstPassUnrecorded: boolean;
  readonly tellsUnrecorded: boolean;
}

function isScenario(name: string | null): name is Scenario {
  return name !== null && Object.prototype.hasOwnProperty.call(SCENARIO_MAP, name);
}

/** Why a draft's stored evidence cannot be read, or null when it can. */
function unreadableReason(d: ShadowDraftEvidence): string | null {
  if (Number.isNaN(d.createdAt.getTime())) return "created_at unreadable";
  if (d.parsedAttempts === null) return "attempts is not an array";
  if (d.parsedAttempts < 1 || d.parsedAttempts > 2) return `${d.parsedAttempts} stored attempts, where compose makes one or two`;
  if (d.verifier === null) return "verifier_result has no readable ok and violations";
  if (d.firstPassClean && d.parsedAttempts !== 1) return "first_pass_clean on more than one attempt";
  if (d.firstPassClean && !d.verifier.ok) return "first_pass_clean on a verifier refusal";
  if (d.firstPassVerifier !== null) {
    if (d.firstPassVerifier.ok !== d.firstPassClean) return "first_pass_verifier_result disagrees with first_pass_clean";
    // A dirty first pass that parsed always gets the retry, so one stored
    // attempt and a dirty first pass means the first attempt never parsed.
    if (!d.firstPassClean && d.parsedAttempts === 1) return "a first-pass verifier result on a first attempt that never parsed";
  }
  if (d.holdingLine === true) {
    if (d.composeModel !== null) return "a holding line with a compose model, which no model writes";
    if (d.parsedAttempts !== 1) return `a holding line on ${d.parsedAttempts} attempts, where the fixed line is one`;
  }
  return null;
}

function read(d: ShadowDraftEvidence): Reading {
  const unreadable = unreadableReason(d);
  const verifier = d.verifier;
  if (unreadable !== null || verifier === null) {
    return { draft: d, unreadable: unreadable ?? "verifier unreadable", breaches: [], figureMismatch: false, firstPassUnrecorded: false, tellsUnrecorded: false };
  }
  const breaches: ShadowBreachKind[] = [];
  if (!verifier.ok || d.status === "blocked") breaches.push("verifier_refused");
  if (RELEASED_STATUSES.has(d.status)) breaches.push("released");
  if (d.leftTheBuilding || d.status === "transmitted") breaches.push("left_the_building");
  if (d.tells !== null && d.tells > 0) breaches.push("ai_tells");
  const figureCodes: ReadonlySet<string> = new Set(SHADOW_FIGURE_CODES);
  const carriesFigure = (v: ShadowVerifierEvidence | null): boolean => v !== null && v.violations.some((c) => figureCodes.has(c));
  return {
    draft: d,
    unreadable: null,
    breaches,
    figureMismatch: carriesFigure(verifier) || carriesFigure(d.firstPassVerifier),
    // Two stored attempts and a dirty first pass: the verifier rejected the
    // first attempt, and on a draft written before migration 0033 its codes
    // went nowhere.
    firstPassUnrecorded: !d.firstPassClean && d.parsedAttempts === 2 && d.firstPassVerifier === null,
    tellsUnrecorded: d.tells === null,
  };
}

/** Anything short of clean, readable, real evidence. */
function isBlocking(r: Reading): boolean {
  return (
    !r.draft.provenReal ||
    r.unreadable !== null ||
    r.breaches.length > 0 ||
    r.figureMismatch ||
    r.firstPassUnrecorded ||
    r.tellsUnrecorded
  );
}

const ID_LIST_CAP = 10;
const NOT_RECORDED = "not recorded";

/** Ids for a blocker line, cut at ten with the count of the rest. */
export function shadowIdList(ids: readonly string[]): string {
  const shown = ids.slice(0, ID_LIST_CAP).join(", ");
  return ids.length > ID_LIST_CAP ? `${shown} and ${ids.length - ID_LIST_CAP} more` : shown;
}

function plural(n: number, one: string, many: string = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function days(ms: number): string {
  return (ms / DAY_MS).toFixed(1);
}

/** The model and versions a draft names, or null when either is not recorded or cannot be read. */
function configurationKey(d: ShadowDraftEvidence): string | null {
  if (d.composeModel === null || d.artifactVersions === null || d.artifactVersions === SHADOW_ARTIFACT_VERSIONS_UNREADABLE) return null;
  return JSON.stringify([d.composeModel, d.artifactVersions]);
}

interface LatestRun {
  readonly configuration: ShadowConfiguration | null;
  /** The drafts every scenario's window may count. */
  readonly draftIds: ReadonlySet<string>;
}

/**
 * The latest run on one configuration, across every draft of the client
 * (decision list item 75). The latest draft names it; the run is every draft
 * later than the last one on anything else. A draft on another configuration
 * at the latest draft's own instant leaves no run: which came last cannot be
 * told, and the window is not given the benefit of the doubt.
 */
function latestRun(drafts: readonly ShadowDraftEvidence[]): LatestRun {
  // A holding line carries no model by construction (TA-467): it neither
  // names a configuration nor ends a run, and no window counts it.
  const dated = drafts.filter((d) => d.holdingLine !== true && !Number.isNaN(d.createdAt.getTime()));
  let latest: ShadowDraftEvidence | undefined;
  for (const d of dated) if (latest === undefined || d.createdAt.getTime() >= latest.createdAt.getTime()) latest = d;
  if (latest === undefined) return { configuration: null, draftIds: new Set() };
  const key = configurationKey(latest);
  const named = { composeModel: latest.composeModel, artifactVersions: latest.artifactVersions };
  if (key === null) return { configuration: { ...named, recorded: false, since: null }, draftIds: new Set() };
  let lastOther = Number.NEGATIVE_INFINITY;
  for (const d of dated) if (configurationKey(d) !== key) lastOther = Math.max(lastOther, d.createdAt.getTime());
  const run = dated.filter((d) => d.createdAt.getTime() > lastOther);
  const since = run.length === 0 ? null : new Date(Math.min(...run.map((d) => d.createdAt.getTime())));
  return { configuration: { ...named, recorded: true, since }, draftIds: new Set(run.map((d) => d.draftId)) };
}

function decideScenario(
  scenario: Scenario,
  readings: readonly Reading[],
  undrafted: number,
  unattributedBlocking: readonly string[],
  unattributedUndrafted: number,
  run: LatestRun,
): ShadowScenarioExit {
  const graduationClass = SCENARIO_MAP[scenario].graduationClass;
  const notProvenReal = readings.filter((r) => !r.draft.provenReal);
  const unreadable = readings.filter((r) => r.draft.provenReal && r.unreadable !== null);
  const measured = readings.filter((r) => r.draft.provenReal && r.unreadable === null);
  // What the configuration composed (TA-467): the window and grounding count
  // these. Every other bar reads all of `measured`, holding lines included.
  const composed = measured.filter((r) => r.draft.holdingLine !== true);

  const byStatus: Record<string, number> = {};
  for (const r of measured) byStatus[r.draft.status] = (byStatus[r.draft.status] ?? 0) + 1;

  // Window, on the latest run only (decision list item 75).
  const inWindow = composed.filter((r) => run.draftIds.has(r.draft.draftId));
  const notCounted = composed.length - inWindow.length;
  const conversations = new Set(inWindow.map((r) => r.draft.threadId)).size;
  const times = inWindow.map((r) => r.draft.createdAt.getTime());
  const spanMs = times.length === 0 ? null : Math.max(...times) - Math.min(...times);
  const windowMet =
    inWindow.length > 0 &&
    (conversations >= SHADOW_EXIT_BARS.minConversations || (spanMs !== null && spanMs >= SHADOW_EXIT_BARS.minSpanMs));

  // Invariants.
  const byKind = Object.fromEntries(SHADOW_BREACH_KINDS.map((k) => [k, 0])) as Record<ShadowBreachKind, number>;
  for (const r of measured) for (const k of r.breaches) byKind[k] += 1;
  const breaching = measured.filter((r) => r.breaches.length > 0);
  const tellsUnrecorded = measured.filter((r) => r.tellsUnrecorded).length;
  // Nothing measured holds nothing: a bar with no evidence is not a pass.
  const invariantsHold = measured.length > 0 && breaching.length === 0 && tellsUnrecorded === 0;

  // Figures.
  const mismatched = measured.filter((r) => r.figureMismatch);
  const firstPassUnrecorded = measured.filter((r) => r.firstPassUnrecorded);
  const figuresHold = measured.length > 0 && mismatched.length === 0 && firstPassUnrecorded.length === 0;

  // Grounding, first pass only, exact counts.
  const firstPassClean = composed.filter((r) => r.draft.firstPassClean).length;
  const total = composed.length + undrafted;
  const groundingHolds = total > 0 && firstPassClean * 100 >= SHADOW_EXIT_BARS.groundingFloorPercent * total;
  // TA-1266: beside grounding, never instead of it.
  const voiceRefused = composed.filter(
    (r) => !r.draft.firstPassClean && (r.draft.firstPassVerifier?.violations ?? []).includes("voice_off_band"),
  );

  // Echo.
  const paired = measured.filter((r) => r.draft.echo.paired);
  const figuresDiffer = measured.filter((r) => r.draft.echo.paired && r.draft.echo.figuresDiffer);

  const blockers: string[] = [];
  if (graduationClass === "D") blockers.push("class D never qualifies (Agent Brains section 9)");
  if (graduationClass === "n/a") blockers.push(`${scenario} generates nothing, so there is no draft to measure`);
  if (measured.length === 0) {
    blockers.push("no shadow drafts measured in this scenario");
  } else if (run.configuration !== null && !run.configuration.recorded) {
    blockers.push(
      "window cannot start: the latest draft's compose model or artifact versions are not recorded, so no run of " +
        "drafts can be shown to share one (decision list item 75, section 11)",
    );
  } else if (!windowMet) {
    const since = run.configuration?.since;
    blockers.push(
      `window not met: ${conversations} of ${SHADOW_EXIT_BARS.minConversations} conversations and ` +
        `${days(spanMs ?? 0)} of ${days(SHADOW_EXIT_BARS.minSpanMs)} days (B6, whichever first)` +
        (notCounted > 0
          ? `, counted from the last compose model or artifact version change` +
            (since ? ` at ${since.toISOString()}` : "") +
            ` (decision list item 75, section 11), so ${plural(notCounted, "earlier draft")} ${notCounted === 1 ? "does" : "do"} not count`
          : ""),
    );
  }
  if (breaching.length > 0) {
    const kinds = SHADOW_BREACH_KINDS.filter((k) => byKind[k] > 0).map((k) => `${k} ${byKind[k]}`).join(", ");
    blockers.push(`${plural(breaching.length, "draft")} breached an invariant (${kinds}): ${shadowIdList(breaching.map((r) => r.draft.draftId))}`);
  }
  if (tellsUnrecorded > 0) {
    blockers.push(
      `no_ai_tells is not recorded on ${plural(tellsUnrecorded, "draft")}: their tells were not stored (written before migration 0033), so the invariant cannot be shown to hold (B14)`,
    );
  }
  if (mismatched.length > 0) {
    blockers.push(`${plural(mismatched.length, "figure mismatch", "figure mismatches")} (B13): ${shadowIdList(mismatched.map((r) => r.draft.draftId))}`);
  }
  if (firstPassUnrecorded.length > 0) {
    blockers.push(
      `${plural(firstPassUnrecorded.length, "draft")} failed the first pass for reasons not stored (written before migration 0033), so a first-pass figure mismatch cannot be ruled out (F-25): ` +
        shadowIdList(firstPassUnrecorded.map((r) => r.draft.draftId)),
    );
  }
  if (total > 0 && !groundingHolds) {
    blockers.push(
      `grounding floor not held: ${firstPassClean} of ${total} first-pass clean, below ${SHADOW_EXIT_BARS.groundingFloorPercent}% (F-25)` +
        (undrafted > 0 ? `, counting ${plural(undrafted, "routed inbound")} with no draft` : ""),
    );
  }
  if (notProvenReal.length > 0) {
    blockers.push(
      `${plural(notProvenReal.length, "draft")} composed by a mock or an unrecorded provider, not evidence (TA-430): ` +
        shadowIdList(notProvenReal.map((r) => r.draft.draftId)),
    );
  }
  if (unreadable.length > 0) {
    blockers.push(
      `${plural(unreadable.length, "draft")} unreadable: ` +
        shadowIdList(unreadable.map((r) => `${r.draft.draftId} (${r.unreadable ?? "unreadable"})`)),
    );
  }
  if (unattributedBlocking.length > 0 || unattributedUndrafted > 0) {
    const parts: string[] = [];
    if (unattributedBlocking.length > 0) parts.push(`${plural(unattributedBlocking.length, "unattributed draft")} (${shadowIdList(unattributedBlocking)})`);
    if (unattributedUndrafted > 0) parts.push(plural(unattributedUndrafted, "routed inbound") + " with no readable scenario");
    blockers.push(`${parts.join(" and ")} carry evidence no scenario can be cleared of`);
  }

  // Decision list item 108: a signal only, so it is counted after the blockers.
  const askedToo = measured.filter((r) => r.draft.contactAskAppended === true && r.draft.modelAskedToo === true);

  return {
    scenario,
    graduationClass,
    qualifies: blockers.length === 0,
    window: { conversations, spanMs, qualifies: windowMet, notCounted },
    invariants: {
      breaches: breaching.length,
      byKind,
      breachDraftIds: breaching.map((r) => r.draft.draftId),
      tellsUnrecorded,
      qualifies: invariantsHold,
    },
    figures: {
      mismatches: mismatched.length,
      mismatchDraftIds: mismatched.map((r) => r.draft.draftId),
      firstPassUnrecorded: firstPassUnrecorded.length,
      firstPassUnrecordedDraftIds: firstPassUnrecorded.map((r) => r.draft.draftId),
      qualifies: figuresHold,
    },
    grounding: { firstPassClean, total, qualifies: groundingHolds },
    voice: { firstPassRefused: voiceRefused.length, draftIds: voiceRefused.map((r) => r.draft.draftId) },
    drafts: {
      measured: measured.length,
      byStatus,
      notProvenReal: notProvenReal.length,
      notProvenRealDraftIds: notProvenReal.map((r) => r.draft.draftId),
      unreadable: unreadable.length,
      unreadableDraftIds: unreadable.map((r) => r.draft.draftId),
      undrafted,
      holdingLines: measured.length - composed.length,
    },
    echo: {
      paired: paired.length,
      unpaired: measured.length - paired.length,
      figuresDiffer: figuresDiffer.length,
      figuresDifferDraftIds: figuresDiffer.map((r) => r.draft.draftId),
    },
    contactAsk: {
      appended: measured.filter((r) => r.draft.contactAskAppended === true).length,
      askedToo: askedToo.length,
      askedTooDraftIds: askedToo.map((r) => r.draft.draftId),
      notRecorded: measured.filter((r) => r.draft.contactAskAppended === null).length,
    },
    agentVersions: [...new Set(measured.map((r) => r.draft.agentVersion))].sort(),
    composeModels: [...new Set(measured.map((r) => r.draft.composeModel ?? NOT_RECORDED))].sort(),
    artifactVersions: [...new Set(measured.map((r) => r.draft.artifactVersions ?? NOT_RECORDED))].sort(),
    blockers,
  };
}

/**
 * Per scenario, each bar with its measured value and whether it qualifies,
 * and every blocker in words. Pure, and report only (B6).
 */
export function decideShadowExit(evidence: ShadowEvidence): ShadowExitReport {
  const readings = evidence.drafts.map(read);
  const byScenario = new Map<Scenario, Reading[]>();
  const unattributed: Reading[] = [];
  const unrecognized = new Set<string>();
  for (const r of readings) {
    const s = r.draft.scenario;
    if (isScenario(s)) {
      const list = byScenario.get(s) ?? [];
      list.push(r);
      byScenario.set(s, list);
    } else {
      if (s !== null) unrecognized.add(s);
      unattributed.push(r);
    }
  }
  const undraftedBy = new Map<Scenario, number>();
  let unattributedUndrafted = 0;
  for (const u of evidence.undrafted) {
    if (isScenario(u.scenario)) undraftedBy.set(u.scenario, (undraftedBy.get(u.scenario) ?? 0) + 1);
    else {
      if (u.scenario !== null) unrecognized.add(u.scenario);
      unattributedUndrafted += 1;
    }
  }
  // An unrecognized name is evidence no scenario can be cleared of, so it blocks whatever else it carries.
  const blockingUnattributed = unattributed
    .filter((r) => r.draft.scenario !== null || isBlocking(r))
    .map((r) => r.draft.draftId);

  const run = latestRun(evidence.drafts);
  const scenarios = (Object.keys(SCENARIO_MAP) as Scenario[]).map((s) =>
    decideScenario(s, byScenario.get(s) ?? [], undraftedBy.get(s) ?? 0, blockingUnattributed, unattributedUndrafted, run),
  );

  return {
    scenarios,
    configuration: run.configuration,
    unattributed: {
      drafts: unattributed.length,
      undrafted: unattributedUndrafted,
      unrecognizedScenarios: [...unrecognized].sort(),
      blockingDraftIds: blockingUnattributed,
    },
    unreadable: readings
      .filter((r) => r.unreadable !== null)
      .map((r) => ({ draftId: r.draft.draftId, reason: r.unreadable ?? "unreadable" })),
    qualifying: scenarios.filter((s) => s.qualifies).map((s) => s.scenario),
    voiceFidelityJudge: "deferred",
  };
}
